import {
  TexasPropertyAuthReq,
  TexasPropertyAuthRes,
  TexasPropertySearchReq,
  TexasPropertySearchRes,
} from '@/types/texas-property'
import { useMutation, useQuery } from '@tanstack/react-query'
import axios, { AxiosError, HttpStatusCode } from 'axios'
import { useEffect } from 'react'

// https://prod-container.trueprodigyapi.com/trueprodigy/cadpublic/auth/token
// https://prod-container.trueprodigyapi.com/public/property/search?page=1&pageSize=20 // main search endpoint
// https://prod-container.trueprodigyapi.com/public/property/searchfulltext?page=1&pageSize=20  // full text search

const authEndpoint = '/trueprodigy/cadpublic/auth/token'
const fullTextSearchPropertyEndpoint = '/public/property/searchfulltext'
const localStorageKey = 'texas-property-auth'

type TexasPropertyAuthData = {
  token: string
  expiresAt: Date
}

const axiosInstance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_TEXAS_PROPERTY_BASE_URL,
})

let authCallback: (() => void) | null = null

axiosInstance.interceptors.request.use((config) => {
  const token = getAuthToken()
  if (token) {
    config.headers.Authorization = `${token}`
  }
  return config
})

axiosInstance.interceptors.response.use(
  (response) => response,
  async (err) => {
    if (
      axios.isAxiosError(err) &&
      err.status === HttpStatusCode.Unauthorized &&
      err.config
    ) {
      localStorage.removeItem(localStorageKey)

      const req: TexasPropertyAuthReq = {
        office: 'Travis',
      }
      const res = await axiosInstance.post<TexasPropertyAuthRes>(
        authEndpoint,
        req,
      )

      localStorage.setItem(
        localStorageKey,
        JSON.stringify({
          token: res.data.user.token,
          expiresAt: new Date(Date.now() + 5 * 60 * 1000),
        }),
      )

      return axiosInstance(err.config)
    }
    return Promise.reject(err)
  },
)

export function useTexasPropertyGetAll(
  searchQuery: string,
  year: string = 'all',
) {
  const page = 1
  const pageSize = 50

  const queryParams = new URLSearchParams({
    page: page.toString(),
    pageSize: pageSize.toString(),
  })

  const { data, isLoading, error, isError } = useQuery<
    TexasPropertySearchRes,
    AxiosError
  >({
    queryKey: ['texas-property-get-all', searchQuery, year],
    queryFn: async () => {
      const req: TexasPropertySearchReq = {
        pYear: {
          operator: year === 'all' ? 'all' : '=',
          value: year,
        },
        fullTextSearch: {
          operator: 'match',
          value: searchQuery,
        },
      }

      const res = await axiosInstance.post<TexasPropertySearchRes>(
        `${fullTextSearchPropertyEndpoint}?${queryParams.toString()}`,
        req,
      )
      return res.data
    },
    enabled: !!searchQuery,
    retry: false,
  })

  return { data, isLoading, error, isError }
}

export function useTexasPropertyAuth() {
  const {
    mutate: onAuthenticate,
    data,
    isPending,
  } = useMutation({
    mutationKey: ['texas-property-auth'],
    mutationFn: async () => {
      const req: TexasPropertyAuthReq = {
        office: 'Travis',
      }

      const res = await axiosInstance.post<TexasPropertyAuthRes>(
        authEndpoint,
        req,
      )

      return res.data
    },
    onSuccess: (data) => {
      const auth: TexasPropertyAuthData = {
        token: data.user.token,
        expiresAt: new Date(Date.now() + 5 * 60 * 1000),
      }

      localStorage.setItem(localStorageKey, JSON.stringify(auth))
    },
  })

  useEffect(() => {
    authCallback = onAuthenticate

    const localData = localStorage.getItem(localStorageKey)
    if (localData) {
      const auth: TexasPropertyAuthData = JSON.parse(localData)
      if (new Date() < new Date(auth.expiresAt)) {
        return
      }
    }

    onAuthenticate()

    const interval = setInterval(onAuthenticate, 4.5 * 60 * 1000)
    return () => {
      clearInterval(interval)
      authCallback = null
    }
  }, [onAuthenticate])

  return { data, isLoading: isPending }
}

function getAuthToken() {
  const localData = localStorage.getItem(localStorageKey)
  if (localData) {
    const auth: TexasPropertyAuthData = JSON.parse(localData)
    if (new Date() < new Date(auth.expiresAt)) {
      return auth.token
    }
  }

  return null
}
